import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import supabase from '../config/supabase';
import CustomModal from './CustomModal';
import ShimmerLoader from './ShimmerLoader';
import { applyInferredScheduleOnComplete } from '../services/inferredScheduleService';
import { persistLastUsedCountFormats } from '../services/countFormatMemoryService';
// FlashingBadge component for visual feedback
function FlashingBadge({ value, flash, backgroundColor, color, children }) {
  return (
    <span
      className={`badge ${flash ? "flash-once" : ""}`}
      style={{ backgroundColor, color }}
    >
      {children || value}
    </span>
  );
}

const CountSession = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [countItems, setCountItems] = useState([]);
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const [tooltipData, setTooltipData] = useState({ show: false, content: '', x: 0, y: 0 });
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [hideTimeout, setHideTimeout] = useState(null);
  const [sortBy, setSortBy] = useState('location'); // 'location', 'vendor', 'category', 'alphabetical'

  const [closeProgress, setCloseProgress] = useState(null);

  useEffect(() => {
    if (!closeProgress || closeProgress.error) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [closeProgress]);

  // Modal state
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [uncountedItemsCount, setUncountedItemsCount] = useState(0);
  const [completeModalMessage, setCompleteModalMessage] = useState('');

  // Flash state for visual feedback
  const [flashPar, setFlashPar] = useState(false);
  const [flashOrder, setFlashOrder] = useState(false);

  // Resolve store id from localStorage/auth utils (avoid undefined globals)
  const resolveCurrentStoreId = () => {
    try {
      if (typeof window.getCurrentStore === 'function') {
        const store = window.getCurrentStore();
        if (store && store.id) return store.id;
      }
    } catch {}
    const stored = localStorage.getItem('selectedStoreId');
    return stored ? parseInt(stored) : null;
  };

  // Counting owns the exact NEED: PAR minus what is on hand, in the item's own
  // unit. No minimum is applied here. A minimum belongs to a VENDOR PRODUCT
  // (purchase_options.moq_packages) and is applied in Savings Review, once a
  // vendor and a package have been chosen — an item bought from four vendors
  // has four minimums, so there was never one true number to apply.
  const calculateNeedQuantity = (shortage) => {
    if (shortage <= 0) return 0;
    return shortage;
  };

  // Initialize component
  useEffect(() => {
    initializeCountSession();
  }, []);

  async function initializeCountSession() {
    try {
      setLoading(true);
      
      // Get session ID from sessionStorage
      const sessionId = sessionStorage.getItem('activeSessionId');
      if (!sessionId) {
        throw new Error('No active session found');
      }

      console.log('🔍 [COUNT-SESSION] Loading session:', sessionId);

      // Resolve store id
      const storeId = resolveCurrentStoreId();
      if (!storeId) {
        throw new Error('No store selected');
      }

      // Fetch session details
      const { data: session, error: sessionError } = await supabase
        .from('count_sessions')
        .select('*')
        .eq('id', sessionId)
        .eq('store_id', storeId)
        .single();

      if (sessionError) {
        throw new Error(`Failed to load session: ${sessionError.message}`);
      }

      if (!session) {
        throw new Error('Session not found or access denied');
      }

      setSelectedSession(session);
      console.log('✅ Session loaded:', session.name);

      // Load count items for this session
      await loadCountItems(sessionId);

    } catch (error) {
      console.error('❌ [COUNT-SESSION] Failed to initialize:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadCountItems(sessionId) {
    try {
      console.log('📦 [COUNT-SESSION] Loading count items for session:', sessionId);

      const { data: items, error } = await supabase
        .from('count_items')
        .select('*')
        .eq('count_session_id', sessionId)
        .order('created_at');

      if (error) {
        throw new Error(`Failed to load count items: ${error.message}`);
      }

      if (!items || items.length === 0) {
        console.log('⚠️ No items found for this session');
        setCountItems([]);
        return;
      }

      // Process items - use the same structure as original CountInterface
      const processedItems = items.map(item => {
        // Parse the item_snapshot which contains the inventory data
        const snapshot = item.item_snapshot || {};
        const currentCount = parseFloat(item.actual_quantity) || 0;
        const parLevel = parseFloat(snapshot.par_level) || 0;
        
        return {
          ...item,
          item_name: snapshot.item_name || 'Unknown Item',
          vendor: snapshot.vendor || 'Unknown',
          category: snapshot.category || 'Unknown',
          par_level: parLevel,
          unit: snapshot.unit || 'each',
          case_size: snapshot.case_size || 1,
          location: snapshot.location || 'Unknown Location',
          current_count: item.actual_quantity || '',
          is_counted: !!item.actual_quantity,
          shortage: Math.max(0, parLevel - currentCount),
          order_quantity: 0 // Will be calculated
        };
      });

      // Calculate order quantities
      processedItems.forEach(item => {
        item.order_quantity = calculateNeedQuantity(item.shortage);
      });

      // Sort items based on current sort preference
      const sortedItems = sortItems(processedItems, sortBy);
      setCountItems(sortedItems);

      console.log('✅ Loaded', sortedItems.length, 'count items');

    } catch (error) {
      console.error('❌ Failed to load count items:', error);
      throw error;
    }
  }

  // Sort items based on preference
  const sortItems = (items, sortMethod) => {
    const sorted = [...items];
    
    switch (sortMethod) {
      case 'location':
        return sorted.sort((a, b) => {
          const locationCompare = (a.location || '').localeCompare(b.location || '');
          if (locationCompare !== 0) return locationCompare;
          return (a.item_name || '').localeCompare(b.item_name || '');
        });
      
      case 'vendor':
        return sorted.sort((a, b) => {
          const vendorCompare = (a.vendor || '').localeCompare(b.vendor || '');
          if (vendorCompare !== 0) return vendorCompare;
          return (a.item_name || '').localeCompare(b.item_name || '');
        });
      
      case 'category':
        return sorted.sort((a, b) => {
          const categoryCompare = (a.category || '').localeCompare(b.category || '');
          if (categoryCompare !== 0) return categoryCompare;
          return (a.item_name || '').localeCompare(b.item_name || '');
        });
      
      case 'alphabetical':
        return sorted.sort((a, b) => (a.item_name || '').localeCompare(b.item_name || ''));
      
      default:
        return sorted;
    }
  };

  // Update count value for current item
  const updateCountValue = async (newValue) => {
    if (!countItems[selectedItemIndex]) return;

    const item = countItems[selectedItemIndex];
    const itemId = item.id;

    try {
      // Update in database
      const { error } = await supabase
        .from('count_items')
        .update({ 
          actual_quantity: newValue,
          is_counted: newValue !== null,
          counted_at: newValue !== null ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', itemId);

      if (error) {
        throw new Error(`Failed to update count: ${error.message}`);
      }

      // Update local state
      const updatedItems = [...countItems];
      const currentCount = parseFloat(newValue) || 0;
      updatedItems[selectedItemIndex] = {
        ...item,
        actual_quantity: newValue,
        current_count: newValue,
        is_counted: newValue !== null,
        shortage: Math.max(0, (item.par_level || 0) - currentCount)
      };
      
      // Recalculate order quantity
      updatedItems[selectedItemIndex].order_quantity = calculateNeedQuantity(
        updatedItems[selectedItemIndex].shortage
      );

      setCountItems(updatedItems);
      console.log('✅ Updated count for:', item.item_name, 'to:', newValue);

    } catch (error) {
      console.error('❌ Failed to update count:', error);
      alert(`Failed to save count: ${error.message}`);
    }
  };

  // Navigation functions
  const goToNextItem = () => {
    if (selectedItemIndex < countItems.length - 1) {
      setSelectedItemIndex(selectedItemIndex + 1);
    }
  };

  const goToPreviousItem = () => {
    if (selectedItemIndex > 0) {
      setSelectedItemIndex(selectedItemIndex - 1);
    }
  };

  const goToItem = (index) => {
    if (index >= 0 && index < countItems.length) {
      setSelectedItemIndex(index);
    }
  };

  // Handle keypad input
  const handleKeypadInput = (value) => {
    if (value === 'clear') {
      updateCountValue(null);
    } else if (value === 'next') {
      goToNextItem();
    } else if (value === 'prev') {
      goToPreviousItem();
    } else if (value === 'enter') {
      // Commit a typed value (including an explicit "0") and advance.
      // If nothing was typed, just advance without marking the item counted.
      const item = countItems[selectedItemIndex];
      if (item && !item.is_counted && item.current_count !== '' && item.current_count !== null) {
        updateCountValue(item.current_count);
      }
      if (selectedItemIndex < countItems.length - 1) {
        goToNextItem();
      }
    } else if (typeof value === 'number') {
      updateCountValue(value);
    }
  };

  // Keyboard listener for Enter key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleKeypadInput('enter');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedItemIndex, countItems]);

  // Show tooltip
  const showTooltip = (content, event) => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    if (hideTimeout) clearTimeout(hideTimeout);

    const timeout = setTimeout(() => {
      const rect = event.target.getBoundingClientRect();
      setTooltipData({
        show: true,
        content,
        x: rect.left + rect.width / 2,
        y: rect.top
      });
    }, 500); // 500ms delay

    setHoverTimeout(timeout);
  };

  // Hide tooltip
  const hideTooltip = () => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    
    const timeout = setTimeout(() => {
      setTooltipData(prev => ({ ...prev, show: false }));
    }, 100); // Small delay to prevent flickering

    setHideTimeout(timeout);
  };

  // Flash effect for badges
  const flashBadge = (type) => {
    switch (type) {
      case 'par':
        setFlashPar(true);
        setTimeout(() => setFlashPar(false), 500);
        break;
      case 'order':
        setFlashOrder(true);
        setTimeout(() => setFlashOrder(false), 500);
        break;
    }
  };

  // Save and complete session
  const completeCountSession = async () => {
    try {
      setCloseProgress({ subStep: 'Finalizing session', error: null });

      const completedAt = new Date().toISOString();
      const { error } = await supabase
        .from('count_sessions')
        .update({
          status: 'completed',
          completed_at: completedAt
        })
        .eq('id', selectedSession.id);

      if (error) {
        throw new Error(`Failed to complete session: ${error.message}`);
      }

      // Completing a count is what sets the template's schedule — see
      // services/inferredScheduleService. Best-effort; never blocks the return.
      await applyInferredScheduleOnComplete(supabase, selectedSession, { completedAt });
      // Remember what package each location was counted in, so the next
      // count opens in it. Best-effort — see countFormatMemoryService.
      await persistLastUsedCountFormats(supabase, selectedSession, { completedAt });

      window.location.href = 'count.html?view=overview';

    } catch (err) {
      console.error('❌ Failed to complete session:', err);
      setCloseProgress({ subStep: null, error: err.message || 'Failed to save count.' });
    }
  };

  // Pause session
  const pauseCountSession = async () => {
    try {
      setLoading(true);

      // Update session status to paused
      const { error } = await supabase
        .from('count_sessions')
        .update({ 
          status: 'paused',
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedSession.id);

      if (error) {
        throw new Error(`Failed to pause session: ${error.message}`);
      }

      console.log('✅ Count session paused');
      
      // Redirect back to calendar
      window.location.href = 'count.html?view=overview';

    } catch (error) {
      console.error('❌ Failed to pause session:', error);
      alert(`Failed to pause session: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Render loading state
  if (loading) {
    return <ShimmerLoader style={{ minHeight: '60vh' }} />;
  }

  // Render error state
  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <div className="alert alert-danger text-center">
          <h4>Error Loading Count Session</h4>
          <p>{error}</p>
          <button 
            className="btn btn-primary me-2" 
            onClick={() => (window.location.href = 'count.html?view=overview')}
          >
            <i className="feather icon-arrow-left"></i> Back to Calendar
          </button>
          <button className="btn btn-outline-secondary" onClick={() => location.reload()}>
            <i className="fas fa-sync-alt"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  // Render empty state
  if (countItems.length === 0) {
    return (
      <div className="container-fluid p-3">
        <div className="alert alert-warning">
          <h4>No Items Found</h4>
          <p className="mb-0">No items found in this count session.</p>
          <button 
            className="btn btn-secondary mt-2" 
            onClick={() => (window.location.href = 'count.html?view=overview')}
          >
            <i className="feather icon-arrow-left"></i> Back to Calendar
          </button>
        </div>
      </div>
    );
  }

  // Main counting interface
  const selectedItem = countItems[selectedItemIndex];
  const countedItems = countItems.filter(item => item.is_counted).length;
  const totalItems = countItems.length;
  const progressPercent = Math.round((countedItems / totalItems) * 100);

  return (
    <div className="container-fluid p-2 h-100" style={{ backgroundColor: '#FEFCFA', display: 'flex', flexDirection: 'column' }}>

      {/* Main Content Area - Scrollable */}
      <div className="row flex-grow-1" style={{ minHeight: 0 }}>
        {/* Item List - Left Side */}
        <div className="col-md-8" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div className="card h-100" style={{ backgroundColor: '#FFFFFF', border: '1px solid #FAF6F2' }}>
            <div className="card-header py-2" style={{ backgroundColor: '#FAF6F2', borderBottom: '1px solid #F5E2D0' }}>
              <div className="d-flex justify-content-between align-items-center">
                <small style={{ color: '#5A4A42', fontWeight: '700', fontSize: '0.85rem' }}>
                  {(() => {
                    const currentItem = countItems[selectedItemIndex];
                    const groupValue = sortBy === 'location' ? currentItem?.location :
                                       sortBy === 'vendor' ? currentItem?.vendor :
                                       sortBy === 'category' ? currentItem?.category : null;
                    if (groupValue) return `Counting ${groupValue}`;
                    return 'Items to Count';
                  })()}
                </small>
                <small style={{ color: '#8B7B73', fontSize: '0.75rem' }}>
                  {countedItems}/{totalItems} counted
                </small>
              </div>
            </div>
            <div className="card-body p-0" style={{ overflow: 'auto' }}>
              <div className="list-group list-group-flush">
                {countItems.map((item, index) => {
                  const groupField = sortBy === 'location' ? 'location' : sortBy === 'vendor' ? 'vendor' : sortBy === 'category' ? 'category' : null;
                  const prevItem = index > 0 ? countItems[index - 1] : null;
                  const showGroupHeader = groupField && (!prevItem || item[groupField] !== prevItem[groupField]);

                  return (
                    <React.Fragment key={item.id}>
                      {showGroupHeader && (
                        <div style={{ padding: '6px 12px', backgroundColor: '#F5E2D0', color: '#5A4A42', fontSize: '0.75rem', fontWeight: '700', borderBottom: '1px solid #E8DDD0' }}>
                          📍 {item[groupField] || 'Uncategorized'}
                        </div>
                      )}
                      <div
                        className={`list-group-item ${index === selectedItemIndex ? 'active' : ''}`}
                        onClick={() => goToItem(index)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: index === selectedItemIndex ? '#C8B5A0' : 'transparent',
                          color: index === selectedItemIndex ? 'white' : '#5A4A42',
                          border: 'none',
                          borderBottom: '1px solid #F5F5F5',
                          padding: '0.5rem 1rem'
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center flex-grow-1" style={{ gap: '8px' }}>
                            {item.is_counted && (
                              <span style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#4CAF50', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.6rem', color: '#fff' }}>✓</span>
                            )}
                            <div>
                              <div className="fw-bold" style={{ fontSize: '0.85rem' }}>
                                {item.item_name}
                              </div>
                              <small style={{ 
                                color: index === selectedItemIndex ? 'rgba(255,255,255,0.8)' : '#8B7B73',
                                fontSize: '0.7rem'
                              }}>
                                {item.location} • {item.vendor}
                              </small>
                            </div>
                          </div>
                          <div className="text-end">
                            {item.is_counted ? (
                              <span className="badge" style={{ backgroundColor: '#4CAF50', color: '#fff', fontSize: '0.75rem' }}>✓ {item.current_count}</span>
                            ) : (
                              <span className="badge" style={{ backgroundColor: '#E8DDD0', color: '#5A4A42', fontSize: '0.7rem' }}>—</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Calculator/Controls - Right Side */}
        <div className="col-md-4" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div className="card h-100" style={{ backgroundColor: '#FFFFFF', border: '1px solid #FAF6F2' }}>
            <div className="card-header py-2" style={{ backgroundColor: '#FAF6F2', borderBottom: '1px solid #F5E2D0' }}>
              <small className="text-muted" style={{ color: '#8B7B73', fontWeight: '600' }}>
                Current Item
              </small>
            </div>
            <div className="card-body d-flex flex-column">
              {/* Current Item Details */}
              <div className="mb-3">
                <h6 className="mb-1" style={{ color: '#5A4A42', fontSize: '1rem' }}>
                  {selectedItem.item_name}
                </h6>
                <small className="text-muted d-block mb-2">{selectedItem.location}</small>
                
                {/* Item Info Badges */}
                <div className="d-flex flex-wrap gap-1 mb-2">
                  <FlashingBadge
                    flash={flashPar}
                    backgroundColor="#E3F2FD"
                    color="#1976D2"
                    onMouseEnter={(e) => showTooltip('Target inventory level (PAR)', e)}
                    onMouseLeave={hideTooltip}
                    onClick={() => flashBadge('par')}
                    style={{ cursor: 'pointer' }}
                  >
                    PAR: {selectedItem.par_level}
                  </FlashingBadge>
                  
                  {/* The MOQ badge that sat here is gone: a vendor minimum is
                      not a counting fact. See docs/moq-ownership-audit.md. */}

                  {selectedItem.shortage > 0 && (
                    <FlashingBadge
                      flash={flashOrder}
                      backgroundColor="#F3E5F5"
                      color="#7B1FA2"
                      onMouseEnter={(e) => showTooltip('What you need: PAR minus what you counted', e)}
                      onMouseLeave={hideTooltip}
                      onClick={() => flashBadge('order')}
                      style={{ cursor: 'pointer' }}
                    >
                      Order: {selectedItem.order_quantity}
                    </FlashingBadge>
                  )}
                </div>

                {/* Current Count Display */}
                <div className="text-center mb-3 p-3" style={{ backgroundColor: '#F8F9FA', borderRadius: '8px' }}>
                  <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>Count</div>
                  <div className="h4 mb-0" style={{ color: '#5A4A42' }}>
                    {selectedItem.current_count !== '' ? selectedItem.current_count : '—'}
                  </div>
                </div>
              </div>

              {/* Calculator Keypad */}
              <div className="flex-grow-1 d-flex flex-column">
                <div className="grid-container mb-3" style={{ flex: 1 }}>
                  {/* Number buttons in 3x3 grid */}
                  <div className="row g-2">
                    {[7, 8, 9, 4, 5, 6, 1, 2, 3].map(num => (
                      <div className="col-4" key={num}>
                        <button
                          className="btn btn-outline-primary w-100"
                          onClick={() => handleKeypadInput(num)}
                          style={{
                            aspectRatio: '1',
                            borderColor: '#D4C4B0',
                            color: '#5A4A42',
                            fontSize: '1.1rem',
                            fontWeight: '600'
                          }}
                        >
                          {num}
                        </button>
                      </div>
                    ))}
                    <div className="col-4">
                      <button
                        className="btn btn-outline-secondary w-100"
                        onClick={() => handleKeypadInput('prev')}
                        style={{
                          aspectRatio: '1',
                          borderColor: '#D4C4B0',
                          color: '#5A4A42',
                          fontSize: '0.8rem'
                        }}
                        disabled={selectedItemIndex <= 0}
                      >
                        <i className="feather icon-arrow-left"></i> Prev
                      </button>
                    </div>
                    <div className="col-4">
                      <button
                        className="btn btn-outline-primary w-100"
                        onClick={() => handleKeypadInput(0)}
                        style={{
                          aspectRatio: '1',
                          borderColor: '#D4C4B0',
                          color: '#5A4A42',
                          fontSize: '1.1rem',
                          fontWeight: '600'
                        }}
                      >
                        0
                      </button>
                    </div>
                    <div className="col-4">
                      <button
                        className="btn btn-success w-100"
                        onClick={() => handleKeypadInput('next')}
                        style={{
                          aspectRatio: '1',
                          fontSize: '0.8rem'
                        }}
                        disabled={selectedItemIndex >= countItems.length - 1}
                      >
                        Next <i className="feather icon-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-grid gap-2">
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleKeypadInput('clear')}
                    style={{
                      borderColor: '#DC3545',
                      color: '#DC3545'
                    }}
                  >
                    <i className="fas fa-eraser"></i> Clear Count
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={pauseCountSession}
                    disabled={loading}
                  >
                    <i className="fas fa-bookmark"></i> Save & Exit
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={completeCountSession}
                    disabled={loading || Boolean(closeProgress)}
                  >
                    <i className="fas fa-check"></i> Complete Count
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Tooltip */}
      {tooltipData.show && (
        <div
          style={{
            position: 'fixed',
            left: tooltipData.x,
            top: tooltipData.y,
            transform: 'translate(-50%, -100%)',
            zIndex: 9999,
            pointerEvents: 'none',
            backgroundColor: '#333',
            color: 'white',
            padding: '0.5rem',
            borderRadius: '4px',
            fontSize: '0.8rem',
            maxWidth: '200px',
            textAlign: 'center'
          }}
        >
          {tooltipData.content}
        </div>
      )}

      {/* Modals */}
      <CustomModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        title="Close Count Session"
        message={completeModalMessage}
        type="confirm"
        onConfirm={pauseCountSession}
        confirmText="Yes"
        cancelText="No"
        confirmButtonStyle="warning"
      />

      <CustomModal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        title="Complete Count Session"
        message={completeModalMessage}
        type="confirm"
        onConfirm={completeCountSession}
        confirmText="Complete"
        cancelText="Cancel"
        confirmButtonStyle="success"
      />

      {closeProgress && (
        <div
          role="status"
          aria-live="polite"
          aria-label={closeProgress.error ? `Save failed: ${closeProgress.error}` : `Saving count${closeProgress.subStep ? `, ${closeProgress.subStep}` : ''}`}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(30,20,15,0.55)',
            zIndex: 12000, display: 'flex', alignItems: 'center', justifyContent: 'center',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <div style={{
            background: '#FEFCFA', borderRadius: '16px', padding: '2rem 2.5rem',
            boxShadow: '0 8px 32px rgba(61,37,24,0.28)', textAlign: 'center', minWidth: '220px',
          }}>
            {closeProgress.error ? (
              <>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
                <div style={{ fontWeight: 700, color: '#5A4A42', marginBottom: '0.4rem' }}>Save failed</div>
                <div style={{ fontSize: '0.85rem', color: '#8B7B73', marginBottom: '1rem' }}>{closeProgress.error}</div>
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() => {
                    setCloseProgress(null);
                    completeCountSession();
                  }}
                >
                  Retry
                </button>
              </>
            ) : (
              <>
                <style>{`@media (prefers-reduced-motion: no-preference) { .cs-pulse { animation: cs-pulse-anim 1.4s ease-in-out infinite; } @keyframes cs-pulse-anim { 0%,100%{opacity:1} 50%{opacity:0.35} } }`}</style>
                <div className="cs-pulse" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📦</div>
                <div style={{ fontWeight: 700, color: '#5A4A42', marginBottom: '0.25rem' }}>Saving count…</div>
                {closeProgress.subStep && (
                  <div style={{ fontSize: '0.82rem', color: '#8B7B73' }}>{closeProgress.subStep}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Auto-mount when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('count-session-container');
  if (container) {
    const root = createRoot(container);
    root.render(<CountSession />);
  }
});

export default CountSession;